import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios'

/**
 * Base API class for MSP NodeJS service communication
 * Provides common HTTP methods and error handling
 *
 * Environment Variables:
 * - VITE_BACKEND_URL: Base URL for the MSP NodeJS service (e.g., 'https://nodeprod.jobsource-msp.com/api')
 * - Falls back to '/api' if not set (for development)
 */
export class BaseApi {
  protected client: AxiosInstance

  constructor(baseURL?: string) {
    this.client = axios.create({
      baseURL: baseURL || import.meta.env.VITE_BACKEND_URL || '/api',
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add request interceptor for authentication
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        let token = null

        try {
          // Try to get from persisted Redux state
          const persistedState = localStorage.getItem('persist:root')
          if (persistedState) {
            const parsed = JSON.parse(persistedState)
            if (parsed.token) {
              // Remove quotes if the token is JSON stringified
              token = parsed.token.replace(/^"|"$/g, '')
            }
          }
        } catch (error) {
          console.error('Error parsing persisted state:', error)
        }

        // ALWAYS check fallback if no valid token found
        if (!token || token === 'null' || token === '') {
          token =
            localStorage.getItem('authToken') ||
            sessionStorage.getItem('authToken') ||
            localStorage.getItem('jwtToken') ||
            sessionStorage.getItem('jwtToken')
        }

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Add response interceptor for common error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      (error) => {
        // Log the error for debugging
        console.error('API Error:', error)

        // Handle common error cases
        if (error.response?.status === 401) {
          // Unauthorized - could redirect to login
          console.warn('Unauthorized access - token may be expired')
        } else if (error.response?.status === 403) {
          // Forbidden - user doesn't have permission
          console.warn('Access forbidden - insufficient permissions')
        } else if (error.response?.status >= 500) {
          // Server error
          console.error('Server error occurred')
        }

        return Promise.reject(error)
      },
    )
  }

  /**
   * Generic GET request
   */
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config)
  }

  /**
   * Generic POST request
   */
  protected async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config)
  }

  /**
   * Generic PUT request
   */
  protected async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config)
  }

  /**
   * Generic DELETE request
   */
  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config)
  }

  /**
   * Generic PATCH request
   */
  protected async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config)
  }
}
